import React, { forwardRef, useCallback, useEffect, useMemo, useState } from 'react';
import { Label } from "@/components/ui/label"
import { TestSuite } from 'store/src/lib/tests/entity';
import { useRaytingDimensions } from '@/utils/useRaytingDimensions';
import { RatingScore } from '@/components/raytd/rating-tool';
import { convertRatingToScore } from '@/app/features/reports/ratingHelpers';
import { useForm, useFormContext } from 'react-hook-form';
import TextWithBreaks from '@/components/raytd/convert-line-break';
import * as RadioGroup from '@radix-ui/react-radio-group';
import { getVersionedAssetUrl } from '@/utils/versioned-asset-url';

export type RatingTypes = 'compliance' | 'compliance_b' | 'condition' | 'general' | 'generic';
export type Rating = 'very_poor' | 'poor' | 'fair' | 'good' | 'very_good';

export type RatingScale = {
  id: number;
  label: string;
  rateable: boolean;

  very_poor: string;
  poor: string;
  fair: string;
  good: string;
  very_good: string;

  organisation_id: number;
  //type: 'single' | 'individual' | 'no_rating';
  type: RatingTypes;
};


export const RATING_LABELS: Rating[] = ['very_poor', 'poor', 'fair', 'good', 'very_good'];

const options = [
  { id: 'very_poor', label: 'Very Poor', style: { top: '20%', left: '5%', width: '25%', height: '60%' } },
  { id: 'poor', label: 'Poor', style: { top: '20%', left: '20%', width: '25%', height: '60%' } },
  { id: 'fair', label: 'Fair', style: { top: '20%', left: '35%', width: '25%', height: '60%' } },
  { id: 'good', label: 'Good', style: { top: '20%', left: '50%', width: '25%', height: '60%' } },
  { id: 'very_good', label: 'Very Good', style: { top: '20%', left: '65%', width: '25%', height: '60%' } },
];

function convertIndexToRating(index: number): Rating {
  return RATING_LABELS[index];
}

interface RaytdResultProps {
  value: string | undefined | null,
  testSuite: TestSuite | null
  width?: number;
}


const RaytdResult = React.memo(({ value, testSuite, width }: RaytdResultProps) => {

  console.info('RaytdResult', value, testSuite?.ratingScale?.type);

  if (!testSuite?.ratingScale) {
    return null;
  }

  const isRatingRequired = testSuite.ratingScale.rateable;

  return (
    <RatingScore
      type={testSuite.ratingScale.type}
      score={convertRatingToScore(value)}
      width={width}
      height={54}
      showSelector={true}
    />
  );

});

const RatingLabel: React.FC<{ text: string }> = React.memo(({ text }) => (
  <div className="text-sm">
    <TextWithBreaks>
      {text}
    </TextWithBreaks>
  </div>
));


interface ResultPickerProps extends RaytdResultProps {
  name: string;
  onChange: (value: string | null) => void,
  onBlur?: () => void,
  allowDeselection?: boolean;
  showClearButton?: boolean;
  allowNotApplicable?: boolean;
}

const RaytdResultPicker = forwardRef<HTMLInputElement, ResultPickerProps>(({
  name,
  width = 230,
  onChange,
  onBlur,
  value,
  testSuite,
  allowDeselection = false,
  showClearButton = false,
  allowNotApplicable = false,

}: ResultPickerProps, ref) => {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(value ? RATING_LABELS.indexOf(value as Rating) : null);
  const [isNotApplicableImageLoaded, setIsNotApplicableImageLoaded] = useState(false);
  const { circleSize, overlap, startX } = useRaytingDimensions(width);

  const formContext = useFormContext();
  const { control, setValue, watch } = formContext || useForm();

  const handleClear = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedIndex(null);
    onChange?.(null);
    onBlur?.();
  }, [onChange, onBlur]);

  const isNotApplicableSelected = allowNotApplicable && !value
  const notApplicableSelectorSrc = isNotApplicableSelected
    ? getVersionedAssetUrl('/reports/ratings/PNG__Compliance_NA_SelectorON.png')
    : getVersionedAssetUrl('/reports/ratings/PNG__Compliance_NA_SelectorOFF.png')

  useEffect(() => {
    setIsNotApplicableImageLoaded(false)
  }, [notApplicableSelectorSrc])

  useEffect(() => {
    console.info('rating updated', value);
    setSelectedIndex(value ? RATING_LABELS.indexOf(value as Rating) : null);
  }, [value]);

  const handleSelection = React.useCallback((value: string) => {
    const index = RATING_LABELS.indexOf(value as Rating);
    if (testSuite?.ratingScale[convertIndexToRating(index)] === '') {
      return; // Do nothing if the rating is not available
    }

    const newSelectedIndex = selectedIndex === index && allowDeselection ? null : index;
    setSelectedIndex(newSelectedIndex);
    onChange?.(newSelectedIndex === null ? null : convertIndexToRating(newSelectedIndex));
    onBlur?.();

    console.debug('rating selected', newSelectedIndex, newSelectedIndex === null ? null : convertIndexToRating(newSelectedIndex));

  }, [selectedIndex, allowDeselection, onChange, testSuite, onBlur]);

  const labelText = useMemo(() => {
    if (selectedIndex === null || !testSuite?.ratingScale?.rateable) {
      return null;
    }
    return testSuite.ratingScale[convertIndexToRating(selectedIndex)];
  }, [selectedIndex, testSuite]);

  const ratingScaleType = testSuite?.ratingScale?.type || '';

  if (!testSuite?.ratingScale) {
    return <Label>Rating scale not loaded</Label>
  }

  return (
    <div
      className="flex flex-col"
      style={{
        minHeight: ratingScaleType === 'generic' || !testSuite.ratingScale.rateable ? 52 : 90,
      }}
    >
      <div className="mb-4 flex items-start gap-2">
        {/* The Radix RadioGroup.Root with Tailwind classes - no overlay so clicks always hit the circles */}
        <RadioGroup.Root
          className="relative overflow-hidden rounded-lg focus-visible-within:border-zinc-950 border border-transparent shrink-0"
          style={{ height: circleSize + 4, width: (width + 8) }}
          value={value ?? undefined}
          onValueChange={handleSelection}
          aria-label="Select an option from the image"
          ref={ref}
          tabIndex={0}
        >
          {/* The background image */}
          <RaytdResult testSuite={testSuite} value={value} width={width} />

          {/* Map over your options to create the invisible buttons */}
          {options.map((option, index) => (
            <RadioGroup.Item
              key={option.id}
              value={option.id}
              id={option.id}
              style={{
                width: circleSize,
                height: circleSize,
                top: 0,
                left: startX + index * (circleSize - overlap),
              }} // Apply position and size styles directly
              aria-label={option.label}
              className={`
              absolute z-10 cursor-pointer rounded-full
              bg-transparent outline-none focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-100
              transition-all
              focus-visible:ring-2 focus-visible:ring-zinc-950
              data-[state=checked]:bg-transparent data-[state=checked]:ring-0 data-[state=checked]:focus-visible:ring-zinc-950
              
            `}
            >
              {/* The Indicator is not visually used but is required by Radix */}
              <RadioGroup.Indicator />
            </RadioGroup.Item>
          ))}
        </RadioGroup.Root>
        {showClearButton && allowNotApplicable ? (
          <button
            type="button"
            className="shrink-0 rounded focus-visible:ring-2 focus-visible:ring-zinc-950 focus-visible:ring-offset-2"
            onClick={handleClear}
            aria-label="Not applicable"
            aria-pressed={isNotApplicableSelected}
          >
            <div className="relative h-[54.31px] w-[54.31px]">
              {!isNotApplicableImageLoaded ? (
                <div
                  className="absolute inset-0 animate-pulse rounded-full bg-zinc-200"
                  aria-hidden="true"
                />
              ) : null}
              <img
                src={notApplicableSelectorSrc}
                alt={isNotApplicableSelected ? 'Not applicable selected' : 'Not applicable'}
                className={`h-[54.31px] w-[54.31px] object-contain transition-opacity ${
                  isNotApplicableImageLoaded ? 'opacity-100' : 'opacity-0'
                }`}
                loading="eager"
                decoding="sync"
                draggable={false}
                onLoad={() => setIsNotApplicableImageLoaded(true)}
                onError={() => setIsNotApplicableImageLoaded(true)}
              />
            </div>
          </button>
        ) : null}
      </div>

      {/* {labelText && <RatingLabel text={labelText} />} */}

      <div className="space-y-2 text-sm flex-grow whitespace-pre-wrap">

        {labelText?.replace(/\\n/g, '\n').split('\n').map((str, index) => (
          <p key={index}>{str}</p>
        ))}

      </div>

    </div>
  );


});

export default React.memo(RaytdResultPicker);

